import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { AiSearchLessonCardFragment } from '@api/graphql-codegen';
import { auGettingStartedGradeLevels, newGettingStartedGradeLevels } from '@constants';
import { useUser } from '@hooks';

/** Matches AI_SEARCH_UNAVAILABLE in parachute-cloud's opAiClient. */
export const AI_SEARCH_UNAVAILABLE = 'AI_SEARCH_UNAVAILABLE';

export type AiLessonCard = AiSearchLessonCardFragment;

export type GradeOption = {
  label: string;
  value: string;
};

export const isAiSearchUnavailable = (error?: ApolloError | null) =>
  Boolean(error?.graphQLErrors?.some((e) => e.extensions?.code === AI_SEARCH_UNAVAILABLE));

/** The list fields are nullable so one malformed lesson cannot blank the whole result. */
export const presentCards = (cards?: (AiLessonCard | null)[] | null): AiLessonCard[] =>
  (cards ?? []).filter((card): card is AiLessonCard => Boolean(card?.lesson?.id));

/**
 * The school's own grades, in school order, as filter options. The value is the grade label itself,
 * which is what the server filters the school's grade collections on.
 *
 * OP-3227: when a Super Admin simulates a school, that school's grades and region instead.
 */
export function useAiGradeOptions(
  school?: { id: string; grades?: string | null }
): { options: GradeOption[]; isAUuser: boolean } {
  const [user] = useUser();
  const isAUuser = Boolean((school?.id ?? user?.id)?.startsWith('AU_'));
  const schoolGrades = school ? school.grades : user?.schools?.[0]?.grades;

  const options = useMemo(() => {
    const order = isAUuser ? auGettingStartedGradeLevels : newGettingStartedGradeLevels;
    let grades: string[] = [];
    try {
      grades = JSON.parse(schoolGrades || '[]');
    } catch {
      grades = [];
    }
    return [...grades]
      .filter((label): label is string => typeof label === 'string' && Boolean(label))
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map((label) => ({ label, value: label }));
  }, [schoolGrades, isAUuser]);

  return { options, isAUuser };
}
